/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement} from 'react';
import isNil from 'lodash.isnil';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {SensorTypes, SmarterTrailerIssueTypes} from '@ion/core/src/types/entities';
import {dateFormatter, timeFormatter} from '@ion/core/src/utils/DateUtil';
import {SmarterTrailerWidgetInfo} from '../../types/types';
import {getTemperatureLabels, getTimeDifference} from './helper';
import WidgetContainer from './WidgetContainer';
import WidgetDescription from './WidgetDescription';

export type TemperatureProps = {
  temperature: SmarterTrailerWidgetInfo;
  sensorType?: SensorTypes;
  icon: ReactElement
};

/**
 * Defines Tire Temperature component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 * import React from 'react';
 * import {TemperatureWidget} from '@ion/dls/src/components/smarterTrailer';
 *
 * <TemperatureWidget wheelTemperature={wheelTemperatureData} />
 *
 * ```
 */
export const TemperatureWidget = ({
  temperature,
  sensorType = SensorTypes.TPMS,
  icon
}: TemperatureProps) => {
  // localization
  const {
    label,
    highTemp,
    normalTemp
  } = getTemperatureLabels(sensorType);

  /**
   * Define a utility to get the Tire Temperature Value
  */
  const getTemperatureValue = ({issues}: SmarterTrailerWidgetInfo): string => {
    const high = issues[SmarterTrailerIssueTypes.highTemperature] || issues[SmarterTrailerIssueTypes.temperatureAlarm];
    return high ? highTemp : normalTemp;
  };

  if (temperature) {
    const {messageTime: timeStamp} = temperature;
    const today       = getTimeDifference(timeStamp);
    const messageDate = dateFormatter(timeStamp, UserPreferences.getSystemDateFormat());
    const messageTime = isNil(today) ? timeFormatter(timeStamp, UserPreferences.getSystemTimeFormat()) : today;
    const value       = getTemperatureValue(temperature);
    const style       = value === highTemp ? {color: 'red'} : {};
    return (
      <WidgetContainer>
        {icon}
        <WidgetDescription label={label}
          value={value}
          messageTime={messageTime}
          messageDate={messageDate}
          valueStyle={style} />
      </WidgetContainer>
    );
  }
  return null;
};

export default TemperatureWidget;
